import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { AppService } from "src/app/app.service";

declare function gridRefresh(eventTarget, event);
declare function OnDefaultSetting(sectionName);

@Component({
  selector: "app-analytics",
  templateUrl: "./analytics.component.html",
  styleUrls: ["./analytics.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class AnalyticsComponent implements OnInit {
  originalModel = { trendYear: "", values: [], clearDimension: false };
  tempModel = { trendYear: "", values: [], clearDimension: false };
  yearList = [];
  actionsDisabled = true;
  showAsPctOfOption = undefined;
  constructor(private appService: AppService) {}

  ngOnInit() {
    /**
     * To apply anakytics on the right click context menu on a line item (NMV, STD, SAR and SCH).
     * Ref : mex-grid-table.component.ts
     */
    window.addEventListener("handleAnalystApply", this.handleAnalystApplyFunc);
    window.addEventListener("MEDataSet", this.MEDatasetFunction);
  }

  ngOnDestroy() {
    window.removeEventListener(
      "handleAnalystApply",
      this.handleAnalystApplyFunc,
    );
    window.removeEventListener("MEDataSet", this.MEDatasetFunction);
  }

  handleAnalystApplyFunc = (data: any) => {
    if (!this.tempModel.values.includes(data.detail.analyticName)) {
      this.tempModel.values.push(data.detail.analyticName);
      this.handleApply(data.detail.event);
    }
  };

  MEDatasetFunction = (rpObj: any) => {
    if (
      rpObj.detail.section === "GrowthTypes" ||
      rpObj.detail.section === "All"
    ) {
      const data = this.getPanelData(rpObj.detail.data.panels);
      this.showAsPctOfOption = rpObj.detail.data.baseparameter.name;
      if (data !== undefined) {
        this.setAnalyticsData(data);
      }
    }
  };

  setAnalyticsData(data) {
    this.originalModel = {
      trendYear: data.trendYear,
      values: this.orderAnalytics(data.values),
      clearDimension: data.clear_dimension,
    };
    this.tempModel = JSON.parse(JSON.stringify(this.originalModel));
    // this.setMEData(this.tempModel);
    this.generateYearList();
    this.actionsDisabled = true;
  }

  generateYearList = () => {
    const periodsIdx = window.MEData.panels.findIndex(
      (panel) => panel.name === "periods",
    );
    const { from, to } = window.MEData.panels[periodsIdx];
    const firstEstimatedYear =
      window.MEData.panels[periodsIdx].first_estimated_year;
    const estStartYear = Number(firstEstimatedYear.match(/\d{4}/));
    const start = from ? Number(from.slice(3, 7)) : 0;
    const end = to ? Number(to.slice(3, 7)) : -1;
    this.yearList = [];
    for (let i = start; i <= end; i++) {
      const isForecast = i >= estStartYear;
      this.yearList.push({
        year: `FY-${i}${isForecast ? "E" : ""}`,
        label: `FY-${i}`,
      });
    }
  };

  onChangeAnalyticInclusion(analyticName: string, isChecked: boolean) {
    if (this.tempModel.values.includes(analyticName) && !isChecked) {
      this.tempModel.values = this.tempModel.values.filter(
        (analytic) => analytic !== analyticName,
      );
    }

    if (!this.tempModel.values.includes(analyticName) && isChecked) {
      this.tempModel.values.push(analyticName);
    }

    this.tempModel.values = this.appService.basicSort(this.tempModel.values);
    this.actionsDisabled = this.appService.compare(
      this.tempModel,
      this.originalModel,
    );
  }

  handleCancel() {
    const panelIdx = this.getIndexForPanel(window.MEData.panels);
    this.setAnalyticsData(window.MEData.panels[panelIdx]);
  }

  handleTrendChange() {
    this.actionsDisabled = this.appService.compare(
      this.tempModel,
      this.originalModel,
    );
  }

  handleApply(evt) {
    const panelIdx = this.getIndexForPanel(window.MEData.panels);
    this.tempModel.clearDimension = true;
    this.setMEData(this.tempModel);
    this.setAnalyticsData(window.MEData.panels[panelIdx]);
    gridRefresh(evt.currentTarget, evt);
  }

  handleClear(evt: MouseEvent) {
    //stop collapsing of inferred data section
    evt.stopImmediatePropagation();
    OnDefaultSetting("GrowthTypes");
  }

  setMEData(analyticsData) {
    const panelIdx = this.getIndexForPanel(window.MEData.panels);
    window.MEData.panels[panelIdx]["type"] =
      analyticsData.values.length === 0 ? "off" : "row";
    window.MEData.panels[panelIdx] = {
      ...window.MEData.panels[panelIdx],
      trendYear: analyticsData.trendYear,
      values: analyticsData.values,
      clear_dimension: analyticsData.clearDimension,
    };

    this.setAnalyticsInRowDimensions();
  }

  setAnalyticsInRowDimensions() {
    const analyticsData = this.getPanelData(window.MEData.panels);

    const analyticsDimensions: any = {
      expansions: this.orderAnalytics(analyticsData.values),
      values: [
        {
          expanded: false,
          name: "Analytics",
        },
      ],
      name: "GrowthTypes",
    };

    if (analyticsData.values.includes("Trend")) {
      analyticsDimensions.trendYear = analyticsData.trendYear;
    }

    const growthTypeIdx = window.MEData.row_dimensions.findIndex(
      (dimension) => dimension.name === "GrowthTypes",
    );

    if (analyticsData.values.length > 0) {
      if (growthTypeIdx > -1) {
        window.MEData.row_dimensions[growthTypeIdx] = analyticsDimensions;
      } else {
        window.MEData.row_dimensions.push(analyticsDimensions);
      }
    } else {
      if (growthTypeIdx > -1) {
        window.MEData.row_dimensions.splice(growthTypeIdx, 1);
      }
    }
  }

  orderAnalytics(analyticsValues) {
    const transform = (analyticType) => {
      const weight = {
        "YoY%": 6,
        "YoY#": 5,
        "Seq%": 4,
        "Seq#": 3,
        Trend: 2,
        "%": 1,
      };
      return weight[analyticType];
    };
    const sorted = this.appService.basicSort(
      analyticsValues,
      [],
      true,
      transform,
    );
    return sorted;
  }

  getPanelData(panelsList) {
    return panelsList.find(
      (panel) => panel.name.toLowerCase() === "growthtypes",
    );
  }

  getIndexForPanel(panelsList) {
    return panelsList.findIndex(
      (panel) => panel.name.toLowerCase() === "growthtypes",
    );
  }
}
